import { Checkbox, Flex } from "@mantine/core";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import ScrollableTable from "src/components/shared/ScrollableTable/ScrollableTable";
import { type ISearchResult } from "src/pages/SearchPage/SearchPage.types";
import { useDispatch, useSelector } from "src/redux/hooks";
import { toggleToExport } from "src/redux/slices/searchSlice";
import { StyledTd, StyledTr } from "src/styled";

import { deepClone, MEDLBID } from "../../../utils";
import { SearchResultsWrapper } from "../../pages/Search/Search.styled";

interface Props {
  data: ISearchResult[];
  loading?: boolean;
  isMedical?: boolean;
  isNormalEntries?: boolean;
  additionalOnNavigation?: () => void;
}

const headers = [
  {
    name: "name",
    label: "Logbook",
    style: { width: "40%" },
  },
  {
    name: "date",
    label: "Open Date",
    style: { width: "10%" },
  },
  {
    name: "entry",
    label: "Entry Date",
    style: { width: "10%" },
  },
  {
    name: "blank",
    label: "",
    style: { width: "10%" },
  },
];

const ResultsTable = ({
  data,
  loading,
  isMedical,
  isNormalEntries,
  additionalOnNavigation,
}: Props) => {
  const navigate = useNavigate();
  const { isDarkMode, uppercase } = useSelector(
    state => state.preferencesSlice,
  );
  const toExport = useSelector(state => state.searchSlice.toExport);
  const { collectionId, logbookId } = useParams();
  const dispatch = useDispatch();
  const parsedHeaders = useMemo(() => {
    const parsed: any[] = deepClone(headers);
    if (!isNormalEntries) {
      parsed.splice(1, 0, {
        name: "mention",
        label: "Mention",
        style: { width: "30%" },
      });
    }
    if (isMedical) {
      parsed[0] = {
        ...headers[0],
        name: "patient",
        label: "Patient",
      };
      parsed[1] = {
        ...parsed[1],
        name: "diagnosis",
        label: "Diagnosis",
      };
    }

    return parsed;
  }, [isMedical, isNormalEntries]);

  const handleNavigate = useCallback(
    (row: ISearchResult) => {
      const idType = row.meid
        ? "meid"
        : row.partid
          ? "partid"
          : row.lbeid
            ? "lbeid"
            : "";
      const lbid = row.lbid ?? logbookId ?? "";
      if (!idType || !lbid) return;

      return () => {
        navigate(`/logbook/${lbid}/${idType}/${row[idType]}`);
        if (additionalOnNavigation) additionalOnNavigation();
      };
    },
    [additionalOnNavigation, logbookId, navigate],
  );

  const handleCheck = useCallback(
    (meid: string) => () => {
      dispatch(toggleToExport(meid));
    },
    [dispatch],
  );

  const renderRow = useCallback(
    ({ row }: { row: ISearchResult }) => (
      <StyledTr background={isDarkMode ? "transparent" : "white"} hover>
        <StyledTd>
          {isMedical ? (
            <p>{row.patient}</p>
          ) : (
            <div>
              {row.lbname}
              {row.notes && (
                <Flex mt={12}>
                  Notes:
                  <Flex direction="column" gap={8} ml={16}>
                    {row.notes.map((note, index) => (
                      <span
                        key={index}
                        style={{
                          textTransform: uppercase ? "uppercase" : "none",
                        }}>
                        {note}
                      </span>
                    ))}
                  </Flex>
                </Flex>
              )}
            </div>
          )}
        </StyledTd>
        {!isNormalEntries && (
          <StyledTd style={{ textTransform: uppercase ? "uppercase" : "none" }}>
            {row.mention ? row.mention : row.diagnosis}
          </StyledTd>
        )}
        <StyledTd center>
          {dayjs(row.datecreated).format("D MMM YYYY")}
        </StyledTd>
        <StyledTd center>
          {row.entrydate ? dayjs(row.entrydate).format("D MMM YYYY") : ""}
        </StyledTd>
        <StyledTd center>
          <Flex align="center" gap="sm" justify="center">
            {collectionId === MEDLBID && (
              <Checkbox
                checked={toExport.includes(row.meid)}
                onChange={handleCheck(row.meid)}
              />
            )}
            <Button onClick={handleNavigate(row)}>View</Button>
          </Flex>
        </StyledTd>
      </StyledTr>
    ),
    [
      collectionId,
      handleCheck,
      handleNavigate,
      isDarkMode,
      isMedical,
      isNormalEntries,
      toExport,
      uppercase,
    ],
  );

  return (
    <SearchResultsWrapper>
      <ScrollableTable
        style={{ padding: 20 }}
        data={data}
        headers={parsedHeaders}
        renderRow={renderRow}
        loading={loading}
        sortable
      />
    </SearchResultsWrapper>
  );
};

export default ResultsTable;
